import React, { useState } from 'react';
import { Mail, Phone, MessageCircle, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { createLead } from '../services/api';

const ContactForm = ({ propertyTitle, propertyId }) => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    mensaje: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      // Crear lead en el backend
      await createLead({
        ...formData,
        propiedadInteres: propertyId || null,
        propiedadTitulo: propertyTitle || 'Consulta general'
      });
      
      setIsSubmitted(true);
      
      // Reset después de 3 segundos
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          nombre: '',
          email: '',
          telefono: '',
          mensaje: ''
        });
      }, 3000);
    } catch (err) {
      console.error('Error enviando formulario:', err);
      setError('Hubo un error al enviar el mensaje. Por favor intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
        <CheckCircle className="text-green-600 mx-auto mb-4" size={48} />
        <h3 className="text-xl font-bold text-green-900 mb-2 font-heading">¡Mensaje Enviado!</h3>
        <p className="text-green-700 font-body">
          Gracias por tu interés. Te contactaremos pronto.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h3 className="text-2xl font-bold text-blue-900 mb-2 font-heading">¿Te interesa esta propiedad?</h3>
      <p className="text-gray-600 mb-6 font-body">Envíanos un mensaje y te responderemos a la brevedad</p>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 font-body">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
            Nombre Completo *
          </label>
          <input
            type="text"
            name="nombre"
            value={formData.nombre}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none font-body"
            placeholder="Tu nombre"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
            <Mail className="inline mr-1" size={16} />
            Email *
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none font-body"
            placeholder="tu@email.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
            <Phone className="inline mr-1" size={16} />
            Teléfono *
          </label>
          <input
            type="tel"
            name="telefono"
            value={formData.telefono}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none font-body"
            placeholder="+52 984 1387001"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
            <MessageCircle className="inline mr-1" size={16} />
            Mensaje
          </label>
          <textarea
            name="mensaje"
            value={formData.mensaje}
            onChange={handleChange}
            rows="4"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none resize-none font-body"
            placeholder="Me interesa esta propiedad. Me gustaría agendar una cita..."
          />
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white py-6 text-lg font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed font-body"
        >
          {isSubmitting ? (
            <>Enviando...</>
          ) : (
            <>
              <Send className="mr-2" size={20} />
              Enviar Mensaje
            </>
          )}
        </Button>

        {/* Contacto directo */}
        <div className="pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600 mb-3 text-center font-body">O contáctanos directamente:</p>
          <div className="flex flex-col gap-2">
            <a
              href="https://wa.me/5215219841387001"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors font-medium font-body"
            >
              <MessageCircle size={20} />
              WhatsApp
            </a>
            <a
              href="tel:+525219841387001"
              className="flex items-center justify-center gap-2 bg-blue-900 text-white py-3 rounded-lg hover:bg-blue-800 transition-colors font-medium font-body"
            >
              <Phone size={20} />
              Llamar Ahora
            </a>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
